'use client';

import type { MouseEvent, ReactNode } from 'react';
import { Check, Eye, FileText, Loader2, Trash2, Wand2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/forms/button';

export interface ResumeListItemData {
  id: string;
  title: string;
  fileName: string;
  fileSize: number;
  fileMime: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  sourceType?: 'uploaded' | 'builder';
  viewUrl?: string;
}

interface ResumeListItemProps {
  resume: ResumeListItemData;
  isSelected?: boolean;
  onSelect?: () => void;
  onView?: (event: MouseEvent<HTMLButtonElement>) => void;
  onRefine?: (event: MouseEvent<HTMLButtonElement>) => void;
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  showViewButton?: boolean;
  showRefineButton?: boolean;
  showDeleteButton?: boolean;
  isRefining?: boolean;
  isDeleting?: boolean;
  isDeleteConfirm?: boolean;
  className?: string;
  icon?: ReactNode;
}

function formatLastUpdatedAt(updatedAt: Date | string): string {
  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) {
    return 'Recently updated';
  }

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ResumeListItem({
  resume,
  isSelected = false,
  onSelect,
  onView,
  onRefine,
  onDelete,
  showViewButton = true,
  showRefineButton = true,
  showDeleteButton = true,
  isRefining = false,
  isDeleting = false,
  isDeleteConfirm = false,
  className,
  icon,
}: ResumeListItemProps) {
  const content = (
    <>
      <div
        className={cn(
          'w-8 h-8 border rounded flex items-center justify-center flex-shrink-0',
          isSelected ? 'border-primary bg-primary' : 'border-border bg-background'
        )}
      >
        {icon ?? (isSelected ? <Check className="h-4 w-4 text-primary-foreground" /> : <FileText className="h-4 w-4 text-muted-foreground" />)}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold leading-snug text-foreground line-clamp-2 break-words [overflow-wrap:anywhere]"
          title={resume.title}
        >
          {resume.title}
        </p>
        <p
          className="text-xs text-muted-foreground truncate [overflow-wrap:anywhere]"
          title={`${resume.fileName} • Updated ${formatLastUpdatedAt(resume.updatedAt || resume.createdAt)}`}
        >
          {resume.fileName} • Updated {formatLastUpdatedAt(resume.updatedAt || resume.createdAt)}
        </p>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'flex max-w-full min-w-0 flex-col gap-3 overflow-hidden p-3 border rounded transition-all sm:flex-row sm:items-start',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:border-primary/50 hover:bg-primary/5',
        className
      )}
    >
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          className="flex flex-1 min-w-0 items-start gap-3 overflow-hidden text-left"
        >
          {content}
        </button>
      ) : (
        <div className="flex flex-1 min-w-0 items-start gap-3 overflow-hidden">{content}</div>
      )}

      <div className="flex w-full flex-wrap items-center gap-2 flex-shrink-0 sm:w-auto sm:justify-end">
        {showRefineButton && onRefine ? (
          <Button
            type="button"
            onClick={onRefine}
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 sm:flex-none"
            title="Refine this resume"
            disabled={isRefining || isDeleting}
          >
            {isRefining ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Wand2 className="h-4 w-4 text-muted-foreground" />
            )}
            Refine
          </Button>
        ) : null}
        {showViewButton && onView ? (
          <Button
            type="button"
            onClick={onView}
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 sm:flex-none"
            title="View resume"
            disabled={isDeleting}
          >
            <Eye className="h-4 w-4 text-muted-foreground" />
            View
          </Button>
        ) : null}
        {showDeleteButton && onDelete ? (
          <Button
            type="button"
            onClick={onDelete}
            variant={isDeleteConfirm ? 'destructive' : 'outline'}
            size="icon"
            className={cn(
              'h-[34px] w-[34px]',
              isDeleteConfirm && 'bg-destructive/10 hover:bg-destructive/20'
            )}
            title={isDeleteConfirm ? 'Click again to confirm delete' : 'Delete resume'}
            aria-label={isDeleteConfirm ? 'Confirm delete resume' : 'Delete resume'}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isDeleteConfirm ? (
              <X className="h-4 w-4" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
